import { OrganizationModule } from '../enums/organization-module.enum';
import { SubscriptionPlan } from '../enums/subscription-plan.enum';

export interface PlanFeature {
  label: string;
  description: string;
  module?: OrganizationModule;
  /** When set, renders as an "Everything from X plan" inherited chip instead of a checklist item. */
  inheritedPlan?: SubscriptionPlan;
}

export const PLAN_FEATURES: Record<SubscriptionPlan, PlanFeature[]> = {
  [SubscriptionPlan.NONE]: [],
  [SubscriptionPlan.STANDARD]: [
    {
      label: $localize`:@@planFeatures.standard.orders:Up to 50 orders per month`,
      description: $localize`:@@planFeatures.standard.orders.desc:Track and manage up to 50 customer orders monthly`,
      module: OrganizationModule.Orders,
    },
    {
      label: $localize`:@@planFeatures.standard.quotes:Quotes with dimensions, price lists and materials`,
      description: $localize`:@@planFeatures.standard.quotes.desc:Generate quotes with full measurement and material data`,
    },
    {
      label: $localize`:@@planFeatures.standard.configurator:Custom product configurator`,
      description: $localize`:@@planFeatures.standard.configurator.desc:Configure products by dimensions and specifications`,
      module: OrganizationModule.Products,
    },
    {
      label: $localize`:@@planFeatures.standard.stages:Order stages: measurement, quote, production, installation, payment`,
      description: $localize`:@@planFeatures.standard.stages.desc:Manage each stage of your order workflow`,
      module: OrganizationModule.Orders,
    },
    {
      label: $localize`:@@planFeatures.standard.customers:Customer database with order and payment history`,
      description: $localize`:@@planFeatures.standard.customers.desc:Store and organize customer info with full history`,
      module: OrganizationModule.Customers,
    },
    {
      label: $localize`:@@planFeatures.standard.portal:Customer portal - up to 3 portal customers`,
      description: $localize`:@@planFeatures.standard.portal.desc:Invite customers to configure and place orders online by themselves`,
      module: OrganizationModule.Customers,
    },
    {
      label: $localize`:@@planFeatures.standard.inventory:Inventory levels and material control`,
      description: $localize`:@@planFeatures.standard.inventory.desc:Track stock levels and manage material quantities`,
      module: OrganizationModule.Inventory,
    },
    {
      label: $localize`:@@planFeatures.standard.stockDeduction:Automatic stock deduction after completion`,
      description: $localize`:@@planFeatures.standard.stockDeduction.desc:Stock is automatically updated when orders are completed`,
    },
    {
      label: $localize`:@@planFeatures.standard.userViews:User view management`,
      description: $localize`:@@planFeatures.standard.userViews.desc:Control what each team member can see and access`,
    },
  ],
  [SubscriptionPlan.OPTIMAL]: [
    {
      label: $localize`:@@planFeatures.optimal.inherited:Everything in Standard`,
      description: $localize`:@@planFeatures.optimal.inherited.desc:All Standard plan features included`,
      inheritedPlan: SubscriptionPlan.STANDARD,
    },
    {
      label: $localize`:@@planFeatures.optimal.unlimitedOrders:No order limit`,
      description: $localize`:@@planFeatures.optimal.unlimitedOrders.desc:Process as many orders as your business needs`,
      module: OrganizationModule.Orders,
    },
    {
      label: $localize`:@@planFeatures.optimal.portal:Customer portal - up to 10 portal customers`,
      description: $localize`:@@planFeatures.optimal.portal.desc:Invite more customers to place their orders online`,
      module: OrganizationModule.Customers,
    },
    {
      label: $localize`:@@planFeatures.optimal.integrations:Integrations with Fakturownia, iFirma and wFirma`,
      description: $localize`:@@planFeatures.optimal.integrations.desc:Connect your accounting system and sync automatically`,
      module: OrganizationModule.Integrations,
    },
    {
      label: $localize`:@@planFeatures.optimal.invoicing:Faster invoicing from order data`,
      description: $localize`:@@planFeatures.optimal.invoicing.desc:Issue invoices directly from data saved in orders`,
    },
    {
      label: $localize`:@@planFeatures.optimal.calendar:Task calendar, deadlines and notifications`,
      description: $localize`:@@planFeatures.optimal.calendar.desc:Plan work and get notified about expiring warranties and deadlines`,
      module: OrganizationModule.Calendar,
    },
    {
      label: $localize`:@@planFeatures.optimal.pdf:PDF document generation`,
      description: $localize`:@@planFeatures.optimal.pdf.desc:Generate quotes and documents for your customers`,
    },
  ],
  [SubscriptionPlan.PRO]: [
    {
      label: $localize`:@@planFeatures.pro.inherited:Everything in Optimal`,
      description: $localize`:@@planFeatures.pro.inherited.desc:All Optimal plan features included`,
      inheritedPlan: SubscriptionPlan.OPTIMAL,
    },
    {
      label: $localize`:@@planFeatures.pro.users:Unlimited additional users`,
      description: $localize`:@@planFeatures.pro.users.desc:Add as many team members as you need`,
    },
    {
      label: $localize`:@@planFeatures.pro.portal:Customer portal without customer limit`,
      description: $localize`:@@planFeatures.pro.portal.desc:Invite as many customers to the portal as you need`,
      module: OrganizationModule.Customers,
    },
    {
      label: $localize`:@@planFeatures.pro.userPrice:Each additional user: 39 PLN net/mo. per person`,
      description: $localize`:@@planFeatures.pro.userPrice.desc:Pay only for the users you add`,
    },
    {
      label: $localize`:@@planFeatures.pro.customRoles:Additional custom roles`,
      description: $localize`:@@planFeatures.pro.customRoles.desc:Add your own roles alongside the built-in ones, with hand-picked permissions`,
    },
    {
      label: $localize`:@@planFeatures.pro.calendarRoles:Per-role calendar access`,
      description: $localize`:@@planFeatures.pro.calendarRoles.desc:Limit team members to their own tasks and deadlines via roles`,
      module: OrganizationModule.Calendar,
    },
    {
      label: $localize`:@@planFeatures.pro.support:Priority support`,
      description: $localize`:@@planFeatures.pro.support.desc:Get faster responses and dedicated help`,
    },
  ],
};

export const MODULE_LABELS: Record<OrganizationModule, string> = {
  [OrganizationModule.Orders]: $localize`:@@module.orders:Orders`,
  [OrganizationModule.Products]: $localize`:@@module.products:Products`,
  [OrganizationModule.Customers]: $localize`:@@module.customers:Customers`,
  [OrganizationModule.Inventory]: $localize`:@@module.inventory:Inventory`,
  [OrganizationModule.Calendar]: $localize`:@@module.calendar:Calendar`,
  [OrganizationModule.Organization]: $localize`:@@module.organization:Organization`,
  [OrganizationModule.Integrations]: $localize`:@@module.integrations:Integrations`,
};
